<template>
    <div
        class="video-stage"
        :style="colorStyle"
    >
        <div
            ref="remote"
            class="video-stage__remote"
            data-test="qr-remote-video"
        ></div>
        <div
            class="video-stage__identity"
            :class="{ 'video-stage__identity--video': remoteVideoOn }"
        >
            <div
                v-if="!remoteVideoOn"
                class="video-stage__avatar"
                data-test="qr-resident-initials"
                aria-hidden="true"
            >
                {{ initials }}
            </div>
            <h1 class="video-stage__name">{{ displayName }}</h1>
            <span
                v-if="duration"
                class="video-stage__duration"
                data-test="qr-call-duration"
            >{{ duration }}</span
            >
            <span
                v-if="!remoteVideoOn && remoteCameraOffLabel"
                class="sr-only"
            >{{ remoteCameraOffLabel }}</span
            >
            <slot />
        </div>

        <div class="video-stage__local">
            <div
                ref="local"
                class="w-full h-full"
                data-test="qr-local-video"
            ></div>
            <div
                v-if="!camOn"
                class="video-stage__placeholder"
            >
                <Icon
                    name="qr-camera-off"
                    class="w-6 h-6"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import Icon from '@/components/ui/Icon';

const props = defineProps({
    badgeColor: {
        type: String,
        default: '',
    },
    displayName: {
        type: String,
        default: '',
    },
    duration: {
        type: String,
        default: '',
    },
    remoteVideoOn: {
        type: Boolean,
        default: false,
    },
    camOn: {
        type: Boolean,
        default: false,
    },
    remoteCameraOffLabel: {
        type: String,
        default: '',
    },
});

const local = ref(null);
const remote = ref(null);

const colorStyle = computed(() => {
    if (!props.badgeColor) return {};
    return {
        '--qr-badge-color': props.badgeColor,
        '--qr-call-background': `radial-gradient(ellipse at 30% 20%, ${props.badgeColor} 0%, #0d121a 78%)`,
    };
});

const initials = computed(() => {
    const parts = props.displayName.trim().split(/\s+/).filter(Boolean);
    return parts
        .filter((part, index) => index === 0 || index === parts.length - 1)
        .map(part => Array.from(part)[0])
        .join('')
        .toUpperCase();
});

function getElements() {
    return { localEl: local.value, remoteEl: remote.value };
}

defineExpose({ getElements });
</script>

<style scoped>
.video-stage {
    position: absolute;
    inset: 0;
    background: var(--qr-call-background, radial-gradient(ellipse at 30% 20%, #bd9d08 0%, #79672e 30%, #0d121a 78%));
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    letter-spacing: 0;
}

.video-stage__remote {
    position: absolute;
    inset: 0;
}

.video-stage__identity {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 22px;
    padding: 24px;
    color: #fff;
    text-align: center;
    pointer-events: none;
}

.video-stage__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 112px;
    height: 112px;
    border-radius: 50%;
    background: var(--qr-badge-color, #d1aa00);
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 40px;
    font-weight: 700;
    line-height: 1;
}

.video-stage__name {
    width: 100%;
    max-width: 480px;
    margin: 0;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    overflow-wrap: anywhere;
}

.video-stage__duration {
    color: #d4d4d4;
    font-size: 14px;
    line-height: 20px;
}

.video-stage__identity--video {
    top: auto;
    align-items: flex-start;
    gap: 8px;
    text-align: left;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.65));
}

.video-stage__local {
    position: absolute;
    top: calc(16px + env(safe-area-inset-top));
    right: calc(16px + env(safe-area-inset-right));
    width: 94px;
    max-width: 28%;
    aspect-ratio: 94 / 128;
    border-radius: 24px;
    overflow: hidden;
    background: #1f1f1f;
}

.video-stage__placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    background: #111;
}

@media (max-height: 500px) {
    .video-stage__identity {
        gap: 10px;
        padding: 12px;
    }

    .video-stage__avatar {
        width: 72px;
        height: 72px;
        font-size: 28px;
    }

    .video-stage__local {
        width: 64px;
        border-radius: 16px;
    }
}
</style>
